import React, { useState, useEffect } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Text } from 'components/ui';
import Carousel from 'components/Carousel';
import RewardCard from './RewardCard';
import BICash from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/BICash';
import BiCashMultiplePromosModal from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/BiCashMultiplePromosModal';
import localeUtils from 'utils/LanguageLocale';
import BeautyInsiderRewardsSummaryEN from './locales/BeautyInsiderRewardsSummary_en_US';
import BeautyInsiderRewardsSummaryFR from './locales/BeautyInsiderRewardsSummary_fr_CA';
import rougeUtils from 'utils/rougeExclusive';
import Location from 'utils/Location';
import basketUtils from 'utils/Basket';
import { showRougeRewardsErrorModal } from 'actions/RougeRewardsActions';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import Actions from 'actions/Actions';
import store from 'store/Store';
import { formatExpirationLocalized } from 'utils/BeautyInsider';
import { space } from 'style/config';
import PromoUtils from 'utils/Promos';

const {
    LINK_TRACKING_EVENT
} = anaConsts;
const BeautyInsiderRewardsSummary = props => {
    const {
        bankRewards,
        availableRrcCoupons,
        basket,
        appliedPromoCodes,
        onApplyRougeReward,
        onRemoveRougeReward,
        onShowLearnMore,
        isDualBasket,
        cbr
    } = props;

    const [isMultiplePromosModalOpen, setIsMultiplePromosModalOpen] = useState(false);

    useEffect(() => {
        if (isMultiplePromosModalOpen && cbr?.error?.isAvailable) {
            store.dispatch(Actions.showInfoModal({ isOpen: false }));
        }
    }, [cbr, isMultiplePromosModalOpen]);
    const rewardsTotal = bankRewards && bankRewards.rewardsTotal;
    const { isGlobalEnabled } = Sephora.fantasticPlasticConfigurations || {};
    const isUS = localeUtils.isUS();
    const isFrench = localeUtils.isFrench();

    const triggerAnalytics = async (couponCodeToApply, errorMessages) => {
        const linkTrackingError = (await import(/* webpackMode: "eager" */ 'analytics/bindings/pages/all/linkTrackingError')).default;

        const analyticsData = PromoUtils.getBasicAnalyticsData(couponCodeToApply, true);

        const fieldErrors = ['beauty offers promo'];
        const data = {
            ...analyticsData,
            bindingMethods: [linkTrackingError],
            fieldErrors,
            errorMessages
        };
        processEvent.process(LINK_TRACKING_EVENT, { data });
    };

    // Handler functions for Rouge Rewards
    const handleApplyRougeReward = (couponCodeToApply, denomination) => {
        // BEP-987: Check if any RRC is already applied, and if so, show error modal and prevent application.
        const anotherRrcApplied = availableRrcCoupons?.some(availableRrcCoupon =>
            appliedPromoCodes?.includes(availableRrcCoupon.couponCode.toLowerCase())
        );

        if (anotherRrcApplied) {
            triggerAnalytics(couponCodeToApply, [getText('multipleRrcError')]);
            store.dispatch(
                showRougeRewardsErrorModal({
                    title: getText('errorTitle'),
                    message: getText('multipleRrcError'),
                    buttonText: getText('okButtonText')
                })
            );

            return;
        }

        // Get basket subtotal (basket.subtotal is a currency string like "$50.00")
        const basketSubTotal = basket?.subtotal ? Number(basketUtils.removeCurrency(basket.subtotal)) : 0;

        // Show error modal if basket is empty or has no value (e.g., only gift cards)
        if (basketUtils.isEmpty(basket) || basketSubTotal === 0) {
            triggerAnalytics(couponCodeToApply, [getText('emptyBasketError')]);
            store.dispatch(
                showRougeRewardsErrorModal({
                    title: getText('errorTitle'),
                    message: getText('emptyBasketError'),
                    buttonText: getText('okButtonText')
                })
            );

            return;
        }

        // Warn if basket value less than reward amount, but still allow application
        if (basketSubTotal < denomination) {
            const remaining = denomination - basketSubTotal;
            const formattedRemaining = isFrench ? `${remaining.toFixed(2)} $` : `$${remaining.toFixed(2)}`;

            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('rougeRewardsTitle'),
                    message: getText('insufficientBasketWarning', [formattedRemaining]),
                    buttonText: getText('okButtonText'),
                    callback: () => {
                        if (onApplyRougeReward) {
                            onApplyRougeReward(couponCodeToApply.toLowerCase());
                        }
                    }
                })
            );

            return;
        }

        if (onApplyRougeReward) {
            onApplyRougeReward(couponCodeToApply.toLowerCase());
        }
    };

    const handleRemoveRougeReward = couponCode => {
        if (onRemoveRougeReward) {
            onRemoveRougeReward(couponCode.toLowerCase());
        }
    };

    const handleLearnMore = coupon => {
        if (onShowLearnMore) {
            onShowLearnMore(coupon);
        }
    };

    // Calculate Rouge exclusive badges
    const rougeExclusiveBadgeText = rougeUtils.isRougeExclusive() ? getText('rougeBadge') : '';

    const rewardCards = [];

    // Rouge Rewards
    const rougeCard = getRougeRewardsCard(
        availableRrcCoupons,
        appliedPromoCodes,
        isFrench,
        rougeExclusiveBadgeText,
        handleApplyRougeReward,
        handleRemoveRougeReward,
        handleLearnMore
    );

    if (rougeCard) {
        rewardCards.push(rougeCard);
    }

    // Credit Card Rewards
    const creditCardCard = getCreditCardRewardsCard(rewardsTotal, isGlobalEnabled, isUS, appliedPromoCodes);

    if (creditCardCard) {
        rewardCards.push(creditCardCard);
    }

    // Beauty Insider Cash
    if (cbr?.isAvailable) {
        rewardCards.push(
            <BICash
                key='beauty-insider-cash'
                onOpenModal={() => setIsMultiplePromosModalOpen(true)}
                cbr={cbr}
                isDualBasket={isDualBasket}
            />
        );
    }

    if (rewardCards.length === 0) {
        return null;
    }

    // Calculate itemWidth based on number of cards
    // Mobile: 315px for multiple items, 100% for single item
    // Large screens: 1/3 width accounting for gaps (gap=3 = 12px)
    const itemWidth =
        rewardCards.length === 1
            ? [`calc(100% - ${space[4]}px)`, `calc((100% - ${space[6] + space[5]}px) / 3)`]
            : [defaultCardWidth, `calc((100% - ${space[6] + space[5]}px) / 3)`];

    return (
        <>
            <Text
                is='h3'
                fontWeight='bold'
                fontSize={['md', 'lg']}
                marginBottom={4}
                paddingTop={[5, 7]}
            >
                {getText('title')}
            </Text>
            <Carousel
                items={rewardCards}
                gap={3}
                size='small'
                paddingY={4}
                marginX={'-container'}
                scrollPadding={[2, 'container']}
                showArrowOnHover={true}
                arrowVariant='circle'
                itemWidth={itemWidth}
            />
            <BiCashMultiplePromosModal
                isOpen={isMultiplePromosModalOpen}
                onDismiss={() => setIsMultiplePromosModalOpen(false)}
                cbr={cbr}
            />
        </>
    );
};

const getText = localeUtils.getLocalisedDataFromResource(BeautyInsiderRewardsSummaryEN, BeautyInsiderRewardsSummaryFR);
const defaultCardWidth = 315;

/**
 * Creates Credit Card Rewards card component
 * @param {number} rewardsTotal - Total credit card rewards amount
 * @param {boolean} isGlobalEnabled - Whether fantastic plastic is globally enabled
 * @param {boolean} isUS - Whether user is in US locale
 * @param {Array} appliedPromoCodes - Array of applied promo codes (lowercase)
 * @returns {React.Element|null} RewardCard component or null
 */
const getCreditCardRewardsCard = (rewardsTotal, isGlobalEnabled, isUS, appliedPromoCodes) => {
    if (!rewardsTotal || !isGlobalEnabled || !isUS) {
        return null;
    }

    // appliedPromoCodes is pre-normalized to lowercase by the viewModel, so filtering and removal are consistent.
    const appliedCCRs = appliedPromoCodes?.filter(promo => PromoUtils.isRewardPromoCode(promo));

    const removeAllCCRs = () => {
        appliedCCRs?.forEach(ccr => PromoUtils.removePromo(ccr, PromoUtils.CTA_TYPES.CCR));
    };

    return (
        <RewardCard
            key='credit-card-rewards'
            testId='ccr-reward-card'
            title={getText('creditCardTitle')}
            primaryText={getText('creditCardPrimaryText', [rewardsTotal])}
            secondaryText={getText('creditCardSecondaryText')}
            buttonText={getText('applyInBasketButtonText')}
            linkText={getText('creditCardLinkText')}
            linkHref='/creditcard'
            onButtonClick={event => Location.navigateTo(event, '/basket')}
            onRemoveClick={removeAllCCRs}
            onLinkClick={event => Location.navigateTo(event, '/creditcard')}
            applied={!!appliedCCRs?.length}
        />
    );
};

/**
 * Creates Rouge Rewards card component
 * @param {Array} availableRrcCoupons - Array of available Rouge Rewards coupons
 * @param {Array} appliedPromoCodes - Array of applied promo codes (lowercase)
 * @param {boolean} isFrench - Whether user is in French locale
 * @param {string} rougeExclusiveBadgeText - Badge text for Rouge exclusive
 * @param {Function} handleApplyRougeReward - Handler for applying Rouge Reward
 * @param {Function} handleRemoveRougeReward - Handler for removing Rouge Reward
 * @param {Function} handleLearnMore - Handler for learn more action
 * @returns {React.Element|null} RewardCard component or null
 */
const getRougeRewardsCard = (
    availableRrcCoupons,
    appliedPromoCodes,
    isFrench,
    rougeExclusiveBadgeText,
    handleApplyRougeReward,
    handleRemoveRougeReward,
    handleLearnMore
) => {
    if (!availableRrcCoupons || availableRrcCoupons.length === 0) {
        return null;
    }

    // Sort coupons: soonest to expire first, then highest denomination
    const sortedCoupons = availableRrcCoupons.slice().sort((a, b) => {
        const dateA = new Date(a.expirationDate);
        const dateB = new Date(b.expirationDate);

        if (dateA - dateB !== 0) {
            return dateA - dateB;
        }

        return b.denomination - a.denomination;
    });

    // Always show the soonest-expiring coupon (per BEP-987)
    const primaryCoupon = sortedCoupons[0];

    const isApplied = appliedPromoCodes?.includes(primaryCoupon.couponCode.toLowerCase());
    const formattedAmount = !isFrench ? `$${primaryCoupon.denomination}` : `${primaryCoupon.denomination} $`;
    const formattedExpiration = formatExpirationLocalized(primaryCoupon.expirationDate, isFrench);

    const secondaryText = formattedExpiration
        ? getText('rougeRewardsExpiresText', [formattedExpiration])
        : getText('rougeRewardsSecondaryText');

    return (
        <RewardCard
            key='rouge-rewards'
            title={getText('rougeRewardsTitle')}
            primaryText={getText('rougeRewardsPrimaryText', [formattedAmount])}
            secondaryText={secondaryText}
            buttonText={getText('rougeRewardsButtonText')}
            linkText={getText('rougeRewardsLinkText')}
            showRougeBadge={!!rougeExclusiveBadgeText}
            applied={isApplied}
            disabled={false}
            onButtonClick={() => handleApplyRougeReward(primaryCoupon.couponCode, primaryCoupon.denomination)}
            onRemoveClick={() => handleRemoveRougeReward(primaryCoupon.couponCode)}
            onLinkClick={() => handleLearnMore(primaryCoupon)}
        />
    );
};

export default wrapFunctionalComponent(BeautyInsiderRewardsSummary, 'BeautyInsiderRewardsSummary');
